import React, { useEffect, useRef, useState } from 'react';
import { NextCta } from '../Layout';
import { cx, reducedMotion, useCopy } from '../core';
import ProductFamily from '../ProductCards';

const copy = {
  en: {
    kicker: 'Products', title: <>AI products built<br />for practical impact.</>,
    intro: 'Three independent products for learning, AI-team design and everyday desktop work — each built around context, clear permissions and human review.',
    jump: 'Jump to product',
    items: [['p-learn', 'Agentec Learn', 'Learning · teaching · research'], ['p-nexus', 'Agentec Nexus', 'AI team setup'], ['p-one', 'Agentec One', 'Personal desktop workspace']],
    composer: 'Describe what you need', send: 'Send ↵',
    lines: [['@Nexus', 'set up a finance approval team'], ['@Learn', 'plan next week for Data Structures'], ['@One', 'summarize today’s tasks and save to memory']],
    explore: 'Explore products'
  },
  zh: {
    kicker: '产品', title: <>面向实际应用的<br />AI 产品。</>,
    intro: '三款独立产品，分别面向学习、AI 团队设计与日常桌面工作——每一款都围绕上下文、清晰的权限和人工审核构建。',
    jump: '跳转到产品',
    items: [['p-learn', 'Agentec Learn', '学习 · 教学 · 科研'], ['p-nexus', 'Agentec Nexus', 'AI 团队搭建'], ['p-one', 'Agentec One', '个人桌面工作台']],
    composer: '描述您的需求', send: '发送 ↵',
    lines: [['@Nexus', '搭建一个财务审批团队'], ['@Learn', '规划下周的数据结构课程'], ['@One', '总结今天的任务并存入记忆']],
    explore: '探索产品'
  }
};

// Decorative ticket stream behind the hero (aria-hidden); each lane repeats once to loop.
const AVATARS = { L: '#7A3A6E', 1: '#E0392F', N: '#1F7BFF', AF: '#5146E5' };
const TAGS = { review: ['#F5A524', 'Human review'], enabled: ['#7A8394', 'Enabled'], approval: ['#F5A524', 'Needs approval'], check: ['#7A8394', 'Check needed'], healthy: ['#3BB273', 'Healthy'], draft: ['#7A8394', 'Draft'], done: ['#3BB273', 'Done'], cited: ['#5146E5', 'Cited'], progress: ['#3BB273', 'In progress'], approved: ['#5146E5', 'Approved v2'], running: ['#5146E5', 'Running'], routed: ['#5146E5', 'Routed'], setup: ['#5146E5', 'Setup'], scheduled: ['#E0392F', 'Scheduled'] };
const LANES = [
  { down: false, items: [['L', 'LRN-100', 'Instructor review requested', 'review'], ['1', 'ONE-137', 'Skill · Web search', 'enabled'], ['N', 'NXS-174', 'Tool permissions · read-only', 'approval'], ['L', 'LRN-211', 'Assignment 2 · Essay feedback', 'check', true], ['N', 'NXS-248', 'Approval required · Send invoice', 'review'], ['1', 'ONE-285', 'Local service · running', 'healthy'], ['N', 'NXS-322', 'Role template · Support assistant', 'draft'], ['1', 'ONE-359', 'Saved to memory · Literature notes', 'done'], ['L', 'LRN-396', 'Research evidence · 3 sources', 'cited']] },
  { down: true, items: [['L', 'LRN-433', 'Learning path · Week 3', 'progress'], ['N', 'NXS-470', 'Approval required · Send invoice', 'review', true], ['L', 'LRN-507', 'Knowledge map · Data Structures', 'approved'], ['1', 'ONE-544', 'Skill · Web search', 'enabled'], ['1', 'ONE-581', 'Conversation · Draft a reply', 'running'], ['L', 'LRN-618', 'Assignment 2 · Essay feedback', 'check'], ['1', 'ONE-655', 'Local service · running', 'healthy'], ['AF', 'AF-692', 'Request routed to the right team', 'routed'], ['N', 'NXS-729', 'Role template · Support assistant', 'draft', true]] },
  { down: false, items: [['L', 'LRN-766', 'Knowledge map · Data Structures', 'approved'], ['N', 'NXS-803', 'Approval required · Send invoice', 'review'], ['1', 'ONE-840', 'Saved to memory · Literature notes', 'done'], ['AF', 'AF-877', 'Request routed to the right team', 'routed'], ['N', 'NXS-914', 'Create AI employee · Finance approval', 'setup'], ['N', 'NXS-951', 'Role template · Support assistant', 'draft'], ['1', 'ONE-108', 'Tasks & schedules · Weekly brief', 'scheduled', true], ['N', 'NXS-145', 'Tool permissions · read-only', 'approval'], ['L', 'LRN-182', 'Instructor review requested', 'review']] },
  { down: true, items: [['L', 'LRN-219', 'Knowledge map · Data Structures', 'approved'], ['1', 'ONE-256', 'Tasks & schedules · Weekly brief', 'scheduled'], ['1', 'ONE-293', 'Conversation · Draft a reply', 'running'], ['1', 'ONE-330', 'Skill · Web search', 'enabled'], ['L', 'LRN-367', 'Assignment 2 · Essay feedback', 'check', true], ['L', 'LRN-404', 'Learning path · Week 3', 'progress'], ['N', 'NXS-441', 'Create AI employee · Finance approval', 'setup'], ['L', 'LRN-478', 'Instructor review requested', 'review'], ['AF', 'AF-515', 'Request routed to the right team', 'routed']] }
];

function Lanes() {
  return <div className="plane">
    {LANES.map((lane, laneIndex) => <div key={laneIndex} className={cx('lane', lane.down && 'down')}>
      {[...lane.items, ...lane.items].map(([avatar, id, title, tag, highlight], index) => {
        const [color, label] = TAGS[tag];
        return <div key={index} className={cx('item', highlight && 'hi')}>
          <div className="r1"><span className="av" style={{ background: AVATARS[avatar] }}>{avatar}</span>{id}</div>
          <div className="ti">{title}</div>
          <div className="r3"><span className="tg"><i style={{ background: color }} />{label}</span><span className="who" /></div>
        </div>;
      })}
    </div>)}
  </div>;
}

// The composer types a request, holds, fades, and loops while the hero is on screen.
function useComposer(composer, typed, lines, pausedRef) {
  const linesRef = useRef(lines);
  linesRef.current = lines;
  useEffect(() => {
    const box = composer.current;
    const out = typed.current;
    if (!box || !out) return undefined;
    const render = (tag, text) => { out.replaceChildren(); const mark = document.createElement('mark'); mark.textContent = tag; out.append(mark, ' ' + text); };
    if (reducedMotion()) { const [tag, text] = linesRef.current[0]; render(tag, text); return undefined; }
    let cancelled = false;
    const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
    const fade = (to, ms) => box.animate([{ opacity: getComputedStyle(box).opacity }, { opacity: to }], { duration: ms, fill: 'forwards', easing: 'cubic-bezier(.4,0,.2,1)' }).finished.catch(() => {});
    (async () => {
      await wait(1600);
      for (let k = 0; !cancelled; k += 1) {
        if (pausedRef.current) { await wait(500); k -= 1; continue; }
        const [tag, text] = linesRef.current[k % linesRef.current.length];
        render(tag, '');
        await fade(1, 600);
        for (let i = 1; i <= text.length && !cancelled; i += 1) { render(tag, text.slice(0, i)); await wait(42); }
        await wait(2200);
        await fade(0, 700);
        await wait(3200);
      }
    })();
    return () => { cancelled = true; box.getAnimations().forEach((animation) => animation.cancel()); };
  }, [composer, typed, pausedRef]);
}

function ProductsHero({ t }) {
  const hero = useRef(null);
  const composer = useRef(null);
  const typed = useRef(null);
  const pausedRef = useRef(false);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { pausedRef.current = !entry.isIntersecting; setPaused(!entry.isIntersecting); });
    observer.observe(hero.current);
    return () => observer.disconnect();
  }, []);
  useComposer(composer, typed, t.lines, pausedRef);
  return <section className="phero" ref={hero} aria-labelledby="h1">
    <div className={cx('scene', paused && 'paused')} aria-hidden="true">
      <div className="glow" />
      <Lanes />
      <div className="composer" ref={composer}><div className="who"><span>AF</span>{t.composer}</div>
        <div className="q"><span className="typed" ref={typed} /><span className="caret" /></div>
        <div className="bar"><i /><i /><i /><b>{t.send}</b></div></div>
      <div className="blur" />
      <div className="fade" />
    </div>
    <div className="wrap inner">
      <div className="copy">
        <p className="kicker" style={{ '--i': 0 }}>{t.kicker}</p>
        <h1 id="h1" style={{ '--i': 1 }}>{t.title}</h1>
      </div>
      <div className="side">
        <p className="intro" style={{ '--i': 2 }}>{t.intro}</p>
        <nav className="jump" style={{ '--i': 3 }} aria-label={t.jump}>
          {t.items.map(([anchor, name, detail], index) => <a key={anchor} href={`#${anchor}`}><span className="n">{`0${index + 1}`}</span><span><b>{name}</b><small>{detail}</small></span><span className="ar" aria-hidden="true">→</span></a>)}
        </nav>
      </div>
    </div>
  </section>;
}

export default function ProductsPage() {
  const { t } = useCopy(copy);
  return <>
    <ProductsHero t={t} />
    <ProductFamily portfolioTo="/products#p-learn" anchors />
    <NextCta secondary={<a className="btn btn-ghost" href="#products">{t.explore} <span className="ar" aria-hidden="true">↗</span></a>} />
  </>;
}
